import { useState } from "react";
import { StandardButton } from "@/components/common/StandardButton";
import PasswordField from "@/components/common/PasswordField";
import { useI18N } from "common/hooks";

interface PasswordGateProps {
  isLoading: boolean;
  hasError: boolean;
  onSubmit: (password: string) => void;
}

export function PasswordGate({
  isLoading,
  hasError,
  onSubmit,
}: PasswordGateProps) {
  const [password, setPassword] = useState("");
  const i18n = useI18N();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (password.trim()) {
      onSubmit(password.trim());
    }
  };

  return (
    <div className="w-full max-w-md mx-auto px-4">
      <div className="bg-white rounded-2xl shadow-sm border border-border-token-brand-minimal p-8 flex flex-col gap-6">
        {/* Icon */}
        <div className="flex justify-center">
          <div className="w-16 h-16 rounded-full bg-container-brand-minimal flex items-center justify-center">
            <svg
              width="32"
              height="32"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <path
                d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z"
                fill="currentColor"
                className="text-fg-brand-subtle"
              />
            </svg>
          </div>
        </div>

        {/* Heading */}
        <div className="text-center flex flex-col gap-2">
          <h2 className="title-m-bold text-fg-neutral-strong">
            {i18n.get("passwordProtectedPage")}
          </h2>
          <p className="label-s-regular text-fg-neutral-subtle">
            {i18n.get("enterPasswordToView")}
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex flex-col gap-4" noValidate>
          <PasswordField
            label={i18n.get("password")}
            placeholder={i18n.get("enterPassword")}
            className="w-full"
            onChange={(e) => setPassword(e.target.value)}
          />

          {hasError && (
            <p role="alert" className="label-s-medium text-red-600">
              {i18n.get("incorrectPassword")}
            </p>
          )}

          <StandardButton
            type="submit"
            variant="primaryNew"
            size="lg"
            fullWidth
            loading={isLoading}
            disabled={!password.trim() || isLoading}
          >
            {i18n.get("unlock")}
          </StandardButton>
        </form>
      </div>
    </div>
  );
}

export default PasswordGate;
